import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import { demoStatus, loadDemoData, clearDemoData } from "../lib/sampleData";
import { demoDataAllowed } from "../lib/demoEnvironment";

/** Business Settings section — load or clear the demo/sample dataset for this workspace. */
export default function DemoDataPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);   // { loaded } | { cleared, residue }

  const refresh = useCallback(() => { demoStatus().then(setStatus).catch(() => setStatus(null)); }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const load = async () => {
    if (busy) return;
    if (!window.confirm("Load a full set of realistic sample data (products, price lists, customers, a full sales cycle, invoices, payments and expenses) into this workspace?")) return;
    setBusy(true); setError(""); setSummary(null); setProgress("Starting…");
    try { await loadDemoData({ onProgress: setProgress }); setSummary({ loaded: true }); refresh(); }
    catch (e) { setError(e.message || "Could not load demo data."); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    if (busy) return;
    if (!window.confirm("Clear the demo data?\n\nMost records are deleted, but issued invoices and credit notes — and the customers/products they reference — are permanent accounting history: they are cancelled/retained, not erased. For a completely fresh start, create a new workspace.")) return;
    setBusy(true); setError(""); setSummary(null); setProgress("Clearing…");
    try { const res = await clearDemoData(); setSummary(res); refresh(); }
    catch (e) { setError(e.message || "Could not clear demo data."); }
    finally { setBusy(false); }
  };

  const canLoad = !!(status && status.workspaceEmpty && !status.seeded);
  const canClear = !!(status && status.seeded);

  // OCT-H2b — on an environment where demo data is not permitted, this is not a
  // feature, so it is not shown. A disabled button with an internal explanation
  // ("only on the test project") is noise to a customer. The one exception is a
  // workspace that is ALREADY seeded: clearDemoData is deliberately unguarded, so
  // the way to clean up a legacy seed must survive.
  if (!demoDataAllowed() && !(status && canClear)) return null;

  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={{ ...S.sectionTitle, marginTop: 0 }}>Demo data</div>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 14, maxWidth: 620 }}>
        Populate this workspace with a realistic Irish wholesaler dataset — products with pack units,
        price lists, customers, a full quote → order → delivery → invoice → payment cycle, a credit note
        and expenses — so every screen comes alive. Ideal for exploring or demonstrating Atlas.
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button style={S.btn()} onClick={load} disabled={busy || !canLoad}>{busy && !summary ? "Loading…" : "✨ Load demo data"}</button>
        <button style={S.btn("secondary")} onClick={clear} disabled={busy || !canClear}>🧹 Clear demo data</button>
      </div>

      {status && !canLoad && !canClear && (
        <div style={{ fontSize: 12, color: C.textDim, marginTop: 8 }}>This workspace already has its own data, so demo data can't be loaded (it only seeds an empty workspace).</div>
      )}
      {canLoad && <div style={{ fontSize: 12, color: C.textDim, marginTop: 8 }}>Available because this workspace is empty.</div>}
      {canClear && <div style={{ fontSize: 12, color: C.textDim, marginTop: 8 }}>Demo data is currently loaded.</div>}

      {busy && <div style={{ fontSize: 12.5, color: C.textMuted, marginTop: 10 }}>⏳ {progress}</div>}
      {error && <div style={{ fontSize: 12.5, color: C.danger, marginTop: 10 }}>⚠ {error}</div>}
      {summary?.loaded && <div style={{ fontSize: 12.5, color: C.success, marginTop: 10 }}>✓ Sample data loaded. Explore the Dashboard, Products, Customers, Invoices and more.</div>}
      {summary?.cleared && (
        <div style={{ fontSize: 12.5, color: C.success, marginTop: 10 }}>
          ✓ Demo data cleared.
          {summary.residue?.length > 0 && (
            <div style={{ color: C.textMuted, marginTop: 6 }}>
              {summary.residue.length} record(s) retained as accounting history — issued invoices/credit notes (and the customers/products they reference) are kept, and price lists are archived. This is normal: financial documents can't be erased. For a clean slate, create a new workspace.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
